import { Currency, Price } from '@uniswap/sdk-core';
import JSBI from 'jsbi';

const ZERO_INPUT_VALUE_REGEX = /^0*\.?0*$/;

/**
 * Validates a numeric input value
 * Allow for empty string and null values
 *
 * @param value - The input value to validate
 * @param maxDecimalPrecision - The maximum number of decimal places allowed
 * @returns Whether the input value is valid
 */
export function isValidNumericInputValue(value?: string | null, maxDecimalPrecision?: number) {
  const inputValue = value ?? '';
  const trailingDigitRegex = maxDecimalPrecision != null ? `{0,${maxDecimalPrecision}}` : '*';
  const validNumericInputRegex = new RegExp(`^\\d*\\.?\\d${trailingDigitRegex}$`);
  return validNumericInputRegex.test(inputValue);
}

/**
 * Parses a numeric input value
 *
 * @param value - The input value to parse
 * @returns The parsed input value
 */
export function parseNumericInputValue(value?: string | null) {
  let inputValue = value ?? '';

  // Allow empty input
  if (inputValue === '' || inputValue === null) {
    return inputValue;
  }

  // Remove leading zeros while preserving decimal numbers (e.g., '02.34' -> '2.34', '0.34' stays '0.34')
  inputValue = inputValue.replace(/^0+(?=\d)/, '');

  // Remove trailing whitespace
  inputValue = inputValue.trim();

  return inputValue;
}

/**
 * Validates a positive numeric input value
 *
 * @param value - The input value to validate
 * @returns Whether the input value is positive
 */
export function isPositiveNumericInputValue(value?: string | null) {
  const isNotPositiveNumericInputValue =
    value == null || value === '' || ZERO_INPUT_VALUE_REGEX.test(value) || Number(value) <= 0;
  return !isNotPositiveNumericInputValue;
}

// reference: https://github.com/Uniswap/interface/blob/53b8ef20fefb95bd6c8e8b5984bc234ca31dba55/apps/web/src/state/mint/v3/utils.ts#L12
export function tryParsePrice<T extends Currency>(baseToken: T, quoteToken: T, value: string) {
  const [whole, fraction] = value.split('.');

  const decimals = fraction?.length ?? 0;
  const withoutDecimals = JSBI.BigInt((whole ?? '') + (fraction ?? ''));

  return new Price(
    baseToken,
    quoteToken,
    JSBI.multiply(JSBI.BigInt(10 ** decimals), JSBI.BigInt(10 ** baseToken.decimals)),
    JSBI.multiply(withoutDecimals, JSBI.BigInt(10 ** quoteToken.decimals)),
  );
}

export function convertNumberToBigIntByDecimals(value: string, valueDecimals: number) {
  if (!isValidNumericInputValue(value) || !isPositiveNumericInputValue(value)) {
    return 0n;
  }

  const [whole, fraction] = value.split('.');

  const decimals = valueDecimals - (fraction?.length ?? 0);
  const withoutDecimals = BigInt((whole ?? '') + (fraction ?? ''));

  return withoutDecimals * BigInt(10 ** decimals);
}
